import { useState, useEffect, useCallback } from "react";

import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";

import "./index.css";
import { checkOtpValid } from "utils/otp.util";
import { requestOTP } from "app/services/dashboard/mfa.service";
import { showToast } from "utils/toast";
import {
  OTP_REQUEST_LIMIT_EXCEEDED_CODE,
  RESEND_OTP_COOLDOWN_SECONDS,
  TOAST_SEVERITY,
} from "static/constants";

import ModalContainer from "components/common/modals/modalContainer/ModalContainer";

export const OTPModal = (props) => {
  const {
    isModalOpen,
    toggleOtpModal,
    handleOTPValidation,
    isEmailVerification = false,
    setMfaStatus,
    mfaStatus = false,
    isDisableMFA = false,
    userEmail = null,
    onOtpRequestBlocked = () => {},
  } = props;

  const [otp, setOtp] = useState("");
  const [isOtpValid, setIsOtpValid] = useState(true);
  const [isOtpEmpty, setIsOtpEmpty] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const modalTitle = `Enter OTP for ${
    isEmailVerification ? "Email" : "Login"
  } Verification`;

  const modalBodyText = `To ${
    isEmailVerification
      ? `${
          isDisableMFA ? "disable" : "enable"
        } Multi-Factor Authentication (MFA)`
      : "verify your login"
  }, a One-Time Password
  (OTP) has been sent to the email associated with this account. Once received, please enter the OTP below.`;

  const handleRequestOTP = useCallback(
    async (isResend) => {
      try {
        const payload = { isResend: isResend || null };
        if ((!isEmailVerification || !isDisableMFA) && userEmail) {
          payload.email = userEmail;
        }
        setResendCooldown(RESEND_OTP_COOLDOWN_SECONDS);
        await requestOTP(payload);
      } catch (error) {
        showToast({ message: error.message, type: TOAST_SEVERITY.ERROR });
        if (
          error?.data?.code === OTP_REQUEST_LIMIT_EXCEEDED_CODE ||
          error?.httpStatus === 403
        ) {
          onOtpRequestBlocked(error.message);
        }
      }
    },
    [userEmail, onOtpRequestBlocked]
  );

  useEffect(() => {
    async function requestForOTP() {
      if (isModalOpen && isEmailVerification) {
        await handleRequestOTP();
      }
    }
    requestForOTP();
  }, [isEmailVerification, isModalOpen, handleRequestOTP]);

  useEffect(() => {
    if (isModalOpen) {
      setResendCooldown(RESEND_OTP_COOLDOWN_SECONDS);
      setOtp("");
      setIsOtpEmpty(false);
      setIsOtpValid(true);
    }
  }, [isModalOpen]);

  useEffect(() => {
    if (resendCooldown <= 0) {
      return;
    }
    const timer = setInterval(() => {
      setResendCooldown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const onClose = () => {
    setMfaStatus(!mfaStatus);
    toggleOtpModal();
  };

  const handleInput = (event) => {
    // Restrict alphabetical characters
    event.target.value = event.target.value.replace(/[^\d]/g, "").slice(0, 6);
    setIsOtpEmpty(false);
    setIsOtpValid(true);
    setOtp(event.target.value);
  };

  const submitOTP = async () => {
    if (!otp || otp.length === 0) {
      return setIsOtpEmpty(true);
    }
    if (!checkOtpValid(otp)) {
      return setIsOtpValid(false);
    }
    try {
      setIsSubmitting(true);
      await handleOTPValidation(otp);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getErrorMessage = () => {
    if (isOtpEmpty) {
      return "Error: OTP cannot be empty.";
    }
    return null;
  };

  return (
    <ModalContainer title={modalTitle} show={isModalOpen} onClose={onClose}>
      <h4 className="otp_instruction_text">{modalBodyText}</h4>
      <TextField
        id="otp"
        label="Enter the OTP sent to your email"
        type="text"
        fullWidth
        value={otp}
        inputProps={{
          onInput: handleInput,
          maxLength: 6,
        }}
        error={getErrorMessage() ? true : false}
        helperText={getErrorMessage()}
        onChange={(e) => {
          setIsOtpValid(checkOtpValid(e.target.value));
        }}
      />
      <div className="d-flex align-items-center justify-between mt-2">
        <span
          className={`otp_resend_text${
            resendCooldown > 0 ? " otp_resend_text--disabled" : ""
          }`}
          onClick={() => resendCooldown <= 0 && handleRequestOTP(true)}
        >
          {resendCooldown > 0
            ? `Resend in ${resendCooldown}s`
            : "Resend One-Time Password"}
        </span>
        <div className="otp-submit-button-container">
          <Button
            variant="contained"
            color="secondary"
            className="otp-submit-button"
            onClick={submitOTP}
            disabled={!isOtpValid || isSubmitting}
          >
            Submit
            {isSubmitting && <CircularProgress size="14px" className="ml-2" />}
          </Button>
        </div>
      </div>
    </ModalContainer>
  );
};
